import { z } from 'zod';
import { idSchema, isoDateTimeSchema, moneySchema, paginationQuerySchema } from './common';
import { userPublicSchema } from './users';

export const ACTIVITY_TYPES = [
  'group_created',
  'group_updated',
  'member_joined',
  'member_added',
  'member_linked',
  'member_removed',
  'member_left',
  'expense_added',
  'expense_edited',
  'expense_deleted',
  'expense_restored',
  'settlement_created',
  'settlement_confirmed',
  'settlement_disputed',
  'settlement_cancelled',
] as const;
export const activityTypeSchema = z.enum(ACTIVITY_TYPES);
export type ActivityType = z.infer<typeof activityTypeSchema>;

/**
 * Shared, per-group history log (no unread state). `data` carries what the sentence needs,
 * snapshotted at the time: e.g. `{ title, amount: {amount,currency} }` for expenses,
 * `{ changes: [{ field, from, to }] }` for edits, `{ memberId, name }` for members,
 * `settlementActivityDataSchema` for settlements, `{ changes }` for groups.
 */
export const activityItemSchema = z.object({
  id: idSchema,
  groupId: idSchema,
  groupName: z.string(),
  type: activityTypeSchema,
  actor: userPublicSchema.nullable(),
  /** Expense, settlement or member id, depending on `type`. */
  subjectId: z.string().nullable(),
  data: z.record(z.string(), z.unknown()),
  createdAt: isoDateTimeSchema,
});
export type ActivityItem = z.infer<typeof activityItemSchema>;

/**
 * `data` of `settlement_*` entries. Names are snapshotted when the entry is written, so
 * history stays accurate after renames or removals. Entries written before these fields
 * existed may lack `fromName`, `toName` and `createdByMemberId`.
 */
export const settlementActivityDataSchema = z.object({
  fromMemberId: idSchema,
  toMemberId: idSchema,
  /** Display name (or placeholder name) of the payer at the time. */
  fromName: z.string().optional(),
  /** Display name (or placeholder name) of the receiver at the time. */
  toName: z.string().optional(),
  /** Member id of whoever recorded the payment (`fromMemberId` or `toMemberId`). */
  createdByMemberId: idSchema.optional(),
  amount: moneySchema,
  /** `settlement_created` only. */
  method: z.string().optional(),
  /** `settlement_created` only: `pending` or `confirmed` (placeholder counterparty). */
  status: z.string().optional(),
});
export type SettlementActivityData = z.infer<typeof settlementActivityDataSchema>;

export const activityQuerySchema = paginationQuerySchema.extend({
  groupId: idSchema.optional(),
});
export type ActivityQuery = z.infer<typeof activityQuerySchema>;
